import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import type { AclRuleDetail, AclUserMatch, AccessType } from '#server/models';
import { useAreaAclUsers, useExportAreaAclUsers } from '../hooks';
import { ColoredBadge } from '../components';
import { ADMIN_SCROLLBAR_CLASSES, ACCESS_TYPE_COLORS } from '../constants';
import { Pagination } from '@/components/Pagination/Pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const formatDate = (d: Date | string) =>
  new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

const PAGE_SIZE = 10;

interface AclUsersPanelProps {
  areaSlug: string;
  rules: AclRuleDetail[];
}

const toggle = <T,>(arr: T[], item: T): T[] =>
  arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

const FilterChip = ({
  label,
  active,
  activeColor,
  onClick,
}: {
  label: string;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
      active
        ? 'border-transparent font-semibold text-[#1A1A1A]'
        : 'border-[#444] text-[#888] hover:text-[#CCC] hover:bg-[#2A2A2A]'
    }`}
    style={active ? { backgroundColor: activeColor ?? '#888' } : undefined}
  >
    {label}
  </button>
);

export const AclUsersPanel = ({ areaSlug, rules }: AclUsersPanelProps) => {
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Search
  const [searchInput, setSearchInput] = useState('');
  const [curtinIdSearch, setCurtinIdSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCurtinIdSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filters
  const [selectedAccessTypes, setSelectedAccessTypes] = useState<AccessType[]>([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => { setPage(1); }, [curtinIdSearch, selectedAccessTypes, selectedUserTypes, selectedRuleIds]);

  const hasActiveFilters =
    !!curtinIdSearch || selectedAccessTypes.length > 0 || selectedUserTypes.length > 0 || selectedRuleIds.length > 0;

  const activeFilters = {
    curtinIdSearch: curtinIdSearch || undefined,
    accessTypes: selectedAccessTypes.length ? selectedAccessTypes : undefined,
    userTypes: selectedUserTypes.length ? selectedUserTypes : undefined,
    ruleIds: selectedRuleIds.length ? selectedRuleIds : undefined,
  };

  const { data, isLoading, isError } = useAreaAclUsers(areaSlug, page, PAGE_SIZE, activeFilters);

  const { refetch: fetchExport, isFetching: isExporting } = useExportAreaAclUsers(areaSlug, activeFilters);

  const handleExport = async () => {
    const { data: rows } = await fetchExport();
    if (!rows) return;
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      'Curtin ID': r.curtinId,
      'User Type': r.userType.charAt(0) + r.userType.slice(1).toLowerCase(),
      'Access Level': r.accessType,
      'Matched Rule': r.matchedRuleName ?? '',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Matched Users');
    XLSX.writeFile(wb, `acl-users-${areaSlug}.xlsx`);
  };

  // Detect unconstrained VIEWER rules — these are noted but not queried
  const unconstrainedViewerRules = rules.filter(
    (r) => r.accessType === 'VIEWER' && r.groups.length === 0 && r.units.length === 0
  );

  // Only constrained rules are meaningful as rule filter options
  const constrainedRules = rules.filter((r) => r.groups.length > 0 || r.units.length > 0);

  const selectedUser = data?.items.find((u) => u.userId === selectedUserId) ?? null;
  const selectedRule = selectedUser ? rules.find((r) => r.id === selectedUser.matchedRuleId) ?? null : null;

  return (
    <div className="flex flex-col h-full min-w-0">
      {/* Header */}
      <div className="flex-shrink-0 pb-3 border-b border-[#2D2D2D]">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Matched users</h2>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="text-xs px-2 py-1 rounded border border-[#444] text-[#999] hover:border-[#C3D82E] hover:text-[#C3D82E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? 'Exporting…' : 'Export xlsx'}
          </button>
        </div>
        {data && (
          <p className="text-sm text-[#999] mt-1">
            {data.total} {data.total === 1 ? 'user' : 'users'}
            {!hasActiveFilters && ' matched by constrained rules'}
          </p>
        )}
      </div>

      {/* System notes */}
      <div className="flex-shrink-0 mt-3 space-y-1.5">
        <Note>
          Super admin users have ADMIN access to all areas and are not listed below.
        </Note>
        {unconstrainedViewerRules.map((rule) => (
          <Note key={rule.id}>
            Unconstrained VIEWER access applies to{' '}
            {rule.userType === 'ANY'
              ? 'all users'
              : rule.userType === 'STAFF'
                ? 'all staff'
                : 'all students'}
            {rule.name ? ` (rule: ${rule.name})` : ''}.
          </Note>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex-shrink-0 mt-3 space-y-2">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search Curtin ID…"
          className="w-full text-xs bg-[#2A2A2A] border border-[#444] rounded px-3 py-1.5 text-[#E8E8E8] placeholder-[#666] focus:outline-none focus:border-[#555]"
        />
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-[#999] w-14 shrink-0">Access</span>
            {(['ADMIN', 'EDITOR', 'VIEWER'] as const).map((t) => (
              <FilterChip
                key={t}
                label={t}
                active={selectedAccessTypes.includes(t)}
                activeColor={ACCESS_TYPE_COLORS[t]}
                onClick={() => setSelectedAccessTypes(toggle(selectedAccessTypes, t))}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-[#999] w-14 shrink-0">User</span>
            {(['STAFF', 'STUDENT'] as const).map((t) => (
              <FilterChip
                key={t}
                label={t.charAt(0) + t.slice(1).toLowerCase()}
                active={selectedUserTypes.includes(t)}
                activeColor="#888"
                onClick={() => setSelectedUserTypes(toggle(selectedUserTypes, t))}
              />
            ))}
          </div>
          {constrainedRules.length > 0 && (
            <div className="flex items-start gap-1.5 flex-wrap">
              <span className="text-xs text-[#999] w-14 shrink-0 pt-0.5">Rule</span>
              <div className="flex flex-wrap gap-1.5">
                {constrainedRules.map((r) => (
                  <FilterChip
                    key={r.id}
                    label={r.name ?? `Rule ${r.id}`}
                    active={selectedRuleIds.includes(r.id)}
                    activeColor="#555"
                    onClick={() => setSelectedRuleIds(toggle(selectedRuleIds, r.id))}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 flex flex-col mt-3">
        {isLoading && <div className="text-sm text-[#999]">Loading users…</div>}
        {isError && <div className="text-sm text-red-400">Failed to load users.</div>}

        {data && data.items.length === 0 && (
          <div className="text-sm text-[#999]">
            {hasActiveFilters ? 'No users match the current filters.' : 'No users match the constrained rules.'}
          </div>
        )}

        {data && data.items.length > 0 && (
          <>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_72px_72px_1fr] gap-3 px-3 pb-2 flex-shrink-0">
              <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Curtin ID</span>
              <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Type</span>
              <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Access</span>
              <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Rule</span>
            </div>

            {/* Rows */}
            <div className={`flex-1 overflow-y-auto ${ADMIN_SCROLLBAR_CLASSES}`}>
              {data.items.map((user) => {
                const color = ACCESS_TYPE_COLORS[user.accessType] ?? '#888';
                return (
                  <button
                    key={user.userId}
                    onClick={() => setSelectedUserId(user.userId)}
                    className="w-full grid grid-cols-[1fr_72px_72px_1fr] gap-3 px-3 py-2.5 rounded hover:bg-[#303030] transition-colors items-center text-left"
                  >
                    <span className="font-mono text-xs text-[#E8E8E8] truncate">{user.curtinId}</span>
                    <span className="text-xs text-[#999] capitalize">{user.userType.toLowerCase()}</span>
                    <ColoredBadge color={color} bold>{user.accessType}</ColoredBadge>
                    <span className="text-xs text-[#999] truncate">
                      {user.matchedRuleName ?? `Rule ${user.matchedRuleId}`}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex-shrink-0 border-t border-[#2D2D2D] pt-1">
                <Pagination page={page} totalPages={data.totalPages} setPage={(p) => { setPage(p); }} compact />
              </div>
            )}
          </>
        )}
      </div>

      {/* User detail dialog */}
      {selectedUser && selectedRule && (
        <UserDetailDialog
          user={selectedUser}
          rule={selectedRule}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
};

const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-2 text-xs text-[#999] border-l-2 border-[#444] pl-2 py-0.5">
    {children}
  </div>
);

const UserDetailDialog = ({
  user,
  rule,
  onClose,
}: {
  user: AclUserMatch;
  rule: AclRuleDetail;
  onClose: () => void;
}) => {
  const color = ACCESS_TYPE_COLORS[user.accessType] ?? '#888';
  const ruleColor = ACCESS_TYPE_COLORS[rule.accessType] ?? '#888';

  const getMembership = (groupId: number) => user.groupMemberships.find((m) => m.groupId === groupId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={`max-w-xl border-[#333] max-h-[80vh] overflow-y-auto ${ADMIN_SCROLLBAR_CLASSES}`}
        style={{ backgroundColor: '#1C1C1C', color: '#E8E8E8' }}
      >
        <DialogHeader>
          <DialogTitle className="text-[#E8E8E8] font-mono">{user.curtinId}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* User info */}
          <dl className="space-y-2 text-sm">
            <div className="flex gap-4 items-center">
              <dt className="text-[#999] w-28 shrink-0">Access level</dt>
              <dd><ColoredBadge color={color} bold>{user.accessType}</ColoredBadge></dd>
            </div>
            <div className="flex gap-4">
              <dt className="text-[#999] w-28 shrink-0">User type</dt>
              <dd className="text-[#E8E8E8] capitalize">{user.userType.toLowerCase()}</dd>
            </div>
          </dl>

          {/* Matched rule */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-[#C3D82E] uppercase tracking-wide">Matched rule</h3>
            <div className="rounded border border-[#2D2D2D] bg-[#222] p-3 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <span className="text-sm font-medium text-[#E8E8E8]">
                  {rule.name ?? 'Unnamed rule'}
                </span>
                <ColoredBadge color={ruleColor} bold>{rule.accessType}</ColoredBadge>
              </div>

              {rule.description && (
                <p className="text-xs text-[#999]">{rule.description}</p>
              )}

              <dl className="space-y-1 text-xs">
                <div className="flex gap-3">
                  <dt className="text-[#999] w-24 shrink-0">User type filter</dt>
                  <dd className="text-[#CCC] capitalize">
                    {rule.userType === 'ANY' ? 'Any' : rule.userType.toLowerCase()}
                  </dd>
                </div>
              </dl>

              {rule.groups.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">
                    Groups (ALL required)
                  </span>
                  <div className="flex flex-col gap-1.5">
                    {rule.groups.map((group, i) => {
                      const membership = getMembership(group.groupId);
                      const hasWindow = membership && (membership.startDate || membership.endDate);
                      const windowLabel = hasWindow
                        ? membership.startDate && membership.endDate
                          ? `${formatDate(membership.startDate)} – ${formatDate(membership.endDate)}`
                          : membership.startDate
                            ? `from ${formatDate(membership.startDate)}`
                            : `until ${formatDate(membership.endDate!)}`
                        : null;
                      return (
                        <span key={group.groupId} className="flex items-center gap-1.5 flex-wrap">
                          {i > 0 && <span className="text-xs font-bold text-[#555]">AND</span>}
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] border border-[#3D3D3D] text-[#CCC]">
                            {group.name}
                          </span>
                          {windowLabel && (
                            <span className="text-xs text-[#666]">{windowLabel}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {rule.groups.length > 0 && rule.units.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-[#2D2D2D]" />
                  <span className="text-xs font-bold text-[#555]">AND</span>
                  <div className="flex-1 h-px bg-[#2D2D2D]" />
                </div>
              )}

              {rule.units.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">
                    Unit enrolments (at least ONE required)
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {rule.units.map((unit, i) => (
                      <span key={unit.unitId} className="flex items-center gap-1.5">
                        {i > 0 && <span className="text-xs font-bold text-[#555]">OR</span>}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] border border-[#3D3D3D] font-mono text-[#CCC]">
                          {unit.udc}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
